"use client";

import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";

type RouteTransitionContextValue = {
  isNavigating: boolean;
  beginNavigation: () => void;
};

const MIN_VISIBLE_MS = 180;
const MAX_PENDING_MS = 8000;

const RouteTransitionContext = createContext<RouteTransitionContextValue | null>(
  null
);

function isEligibleAnchor(anchor: HTMLAnchorElement) {
  if (!anchor.href || anchor.target === "_blank" || anchor.hasAttribute("download")) {
    return false;
  }

  if (anchor.dataset.noRouteTransition === "true") {
    return false;
  }

  const url = new URL(anchor.href, window.location.href);

  if (url.origin !== window.location.origin) {
    return false;
  }

  return (
    url.pathname !== window.location.pathname ||
    url.search !== window.location.search
  );
}

function GlobalRouteFeedback({ active }: { active: boolean }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[120]">
      <div
        className={cn(
          "absolute inset-0 transition-opacity duration-200",
          active ? "opacity-100" : "opacity-0"
        )}
        style={{ backgroundColor: "rgba(15, 23, 42, 0.2)" }}
      />

      <div
        className={cn(
          "absolute inset-x-0 top-0 overflow-hidden bg-primary/12 transition-opacity duration-150",
          active ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="h-1.5 w-1/3 rounded-full bg-primary shadow-[0_0_24px_rgba(105,125,255,0.45)] animate-route-loading" />
      </div>

      <div className="flex min-h-screen items-center justify-center px-4">
        <div
          className={cn(
            "relative w-full max-w-sm rounded-3xl px-6 py-5 text-center transition-opacity duration-150",
            active
              ? "opacity-100"
              : "opacity-0"
          )}
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            boxShadow: "0 24px 80px rgba(15, 23, 42, 0.24)",
          }}
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <div
              className="h-7 w-7 animate-spin rounded-full border-[3px]"
              style={{
                borderColor: "rgba(105, 125, 255, 0.2)",
                borderTopColor: "rgb(105, 125, 255)",
              }}
            />
          </div>

          <p className="mt-4 text-base font-semibold text-slate-900">
            Loading please wait...
          </p>
        </div>
      </div>
    </div>
  );
}

function RouteKeyObserver({
  onRouteKeyChange,
}: {
  onRouteKeyChange: (routeKey: string) => void;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeKey = useMemo(
    () => `${pathname}?${searchParams.toString()}`,
    [pathname, searchParams]
  );

  useEffect(() => {
    onRouteKeyChange(routeKey);
  }, [onRouteKeyChange, routeKey]);

  return null;
}

export function RouteTransitionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [routeKey, setRouteKey] = useState("");
  const routeKeyRef = useRef(routeKey);
  const navigationStartedAtRef = useRef<number | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  const beginNavigation = useCallback(() => {
    navigationStartedAtRef.current = performance.now();
    setIsNavigating(true);
  }, []);

  useEffect(() => {
    function getAnchorTarget(target: EventTarget | null) {
      if (!(target instanceof Element)) {
        return null;
      }

      const anchor = target.closest("a[href]");

      return anchor instanceof HTMLAnchorElement ? anchor : null;
    }

    function handlePointerNavigationStart(
      event: MouseEvent | PointerEvent | TouchEvent
    ) {
      if (event.defaultPrevented) {
        return;
      }

      if ("button" in event && event.button !== 0) {
        return;
      }

      if (
        "metaKey" in event &&
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      const anchor = getAnchorTarget(event.target);

      if (!(anchor instanceof HTMLAnchorElement) || !isEligibleAnchor(anchor)) {
        return;
      }

      beginNavigation();
    }

    function handlePopState() {
      beginNavigation();
    }

    document.addEventListener("pointerdown", handlePointerNavigationStart, true);
    document.addEventListener("mousedown", handlePointerNavigationStart, true);
    document.addEventListener("touchstart", handlePointerNavigationStart, true);
    window.addEventListener("popstate", handlePopState);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerNavigationStart,
        true
      );
      document.removeEventListener(
        "mousedown",
        handlePointerNavigationStart,
        true
      );
      document.removeEventListener(
        "touchstart",
        handlePointerNavigationStart,
        true
      );
      window.removeEventListener("popstate", handlePopState);
    };
  }, [beginNavigation]);

  useEffect(() => {
    if (routeKeyRef.current === routeKey) {
      return;
    }

    routeKeyRef.current = routeKey;

    if (!isNavigating) {
      return;
    }

    const startedAt = navigationStartedAtRef.current ?? performance.now();
    const elapsed = performance.now() - startedAt;
    const remainingMs = Math.max(0, MIN_VISIBLE_MS - elapsed);

    const timeoutId = window.setTimeout(() => {
      navigationStartedAtRef.current = null;
      setIsNavigating(false);
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isNavigating, routeKey]);

  useEffect(() => {
    if (!isNavigating) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      navigationStartedAtRef.current = null;
      setIsNavigating(false);
    }, MAX_PENDING_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isNavigating]);

  const value = useMemo(
    () => ({
      isNavigating,
      beginNavigation,
    }),
    [beginNavigation, isNavigating]
  );

  return (
    <RouteTransitionContext.Provider value={value}>
      <GlobalRouteFeedback active={isNavigating} />
      <Suspense fallback={null}>
        <RouteKeyObserver onRouteKeyChange={setRouteKey} />
      </Suspense>
      {children}
    </RouteTransitionContext.Provider>
  );
}

export function useRouteTransition() {
  const context = useContext(RouteTransitionContext);

  if (!context) {
    throw new Error(
      "useRouteTransition must be used within RouteTransitionProvider"
    );
  }

  return context;
}
